import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select, Switch } from 'antd';

import HVACSpacesContext from '@totem/components/portfolio/buildings/hvac/HVACSpacesContext';
import {
  HVACSpace,
  SPACE_TYPES,
} from '@totem/components/portfolio/buildings/hvac/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const { Option } = Select;

const styles = {
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
    width: '100%',
  },
};

type Props = {
  open: boolean;
  space?: HVACSpace;
  onClose: () => void;
};

const emptySpace = {
  name: '',
  type: 'floor',
  occupied: true,
  squareFeet: null as number,
  primaryEquipment: [] as string[],
};

const HVACSpaceModal = ({ open, space, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const { buildingId, requestRefresh } = useContext(HVACSpacesContext);
  const [isSending, setIsSending] = useState(false);
  const [form, setForm] = useState(emptySpace);

  const isEditing = !!space;

  useEffect(() => {
    if (open) {
      setForm(
        space
          ? {
              name: space.name,
              type: space.type,
              occupied: space.occupied,
              squareFeet: space.squareFeet ?? null,
              primaryEquipment: space.primaryEquipment ?? [],
            }
          : emptySpace,
      );
    }
  }, [open, space]);

  const handleSubmit = useCallback(async () => {
    if (!form.name.trim()) {
      return;
    }

    if (isSending) {
      return;
    }

    setIsSending(true);

    const url = isEditing
      ? `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/hvac-spaces/${space.id}`
      : `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/hvac-spaces`;

    const method = isEditing ? 'PATCH' : 'POST';

    fetch(url, {
      method,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(form),
    })
      .then((res) => {
        CheckResponseShowError(res);

        requestRefresh();

        onClose();
      })
      .finally(() => {
        setIsSending(false);
      });
  }, [
    form,
    isSending,
    isEditing,
    buildingId,
    space,
    getApiToken,
    requestRefresh,
    onClose,
  ]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <ModalTitle>
          {isEditing ? 'Edit HVAC Space' : 'Add HVAC Space'}
        </ModalTitle>
      }
      footer={
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={isSending}
            disabled={!form.name.trim()}
            onClick={handleSubmit}
          >
            {isEditing ? 'Save' : 'Add'}
          </Button>
        </ModalFooter>
      }
    >
      <Form layout="vertical">
        <Form.Item label="Name" style={styles.formItem} required>
          <Input
            value={form.name}
            onChange={(evt) => setForm({ ...form, name: evt.target.value })}
            placeholder="e.g. Floor 3, Zone A"
          />
        </Form.Item>

        <Form.Item label="Type" style={styles.formItem}>
          <Select
            value={form.type}
            onChange={(value) => setForm({ ...form, type: value })}
          >
            {SPACE_TYPES.map((spaceType) => (
              <Option key={spaceType.value} value={spaceType.value}>
                {spaceType.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Square Feet" style={styles.formItem}>
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            value={form.squareFeet}
            onChange={(value) => setForm({ ...form, squareFeet: value })}
            placeholder="Optional"
          />
        </Form.Item>

        <Form.Item label="Occupied" style={styles.formItem}>
          <Switch
            checked={form.occupied}
            onChange={(checked) => setForm({ ...form, occupied: checked })}
          />
        </Form.Item>

        <Form.Item label="Primary Equipment" style={styles.formItem}>
          <Select
            mode="tags"
            style={{ width: '100%' }}
            value={form.primaryEquipment}
            onChange={(value) => setForm({ ...form, primaryEquipment: value })}
            placeholder="Type equipment name and press Enter"
            tokenSeparators={[',']}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default HVACSpaceModal;
